import { useState } from "react";
import type { AiModel } from "@/lib/queries";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatPricing } from "./modelPricing";

/** Sentinel picker value meaning "free-text custom slug" (not in the live list). */
export const CUSTOM_MODEL = "__custom__";

interface ModelPickerProps {
  models: AiModel[];
  /** Current model slug. When not found in `models`, the picker shows the custom state. */
  value: string;
  /** Called with a model slug, or CUSTOM_MODEL when "Custom model slug…" is picked. */
  onChange: (v: string) => void;
  /** Pricing display mode (per-token vs per-hour/per-second heuristic). */
  isTranscription: boolean;
  disabled?: boolean;
  /** True while the live model list is loading. */
  loading?: boolean;
  /** Fallback message when the list is empty (e.g. OpenRouter unreachable). */
  message?: string;
}

/**
 * Searchable model combobox for the AI settings card (replaces the plain
 * Select dropdowns — 469 text / 26 STT models don't fit a static list).
 * Popover + Command (cmdk) pattern; filtering is done manually (substring on
 * id + name, case-insensitive) so the pinned "Custom model slug…" item is
 * never filtered out. The list uses the visible-scrollbar utility (Windows
 * overlay scrollbars auto-hide — see post-mortem 2026-10-08).
 */
export function ModelPicker({
  models,
  value,
  onChange,
  isTranscription,
  disabled,
  loading,
  message,
}: ModelPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const q = query.trim().toLowerCase();
  const filtered = q
    ? models.filter(
        (m) =>
          m.id.toLowerCase().includes(q) ||
          m.name.toLowerCase().includes(q),
      )
    : models;
  const selected = models.find((m) => m.id === value) ?? null;
  const isCustom = !selected;

  const pick = (v: string) => {
    onChange(v);
    setOpen(false);
    setQuery("");
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="h-10 w-full justify-between font-normal"
        >
          <span className="flex min-w-0 items-center">
            {selected ? (
              <span className="truncate font-medium">{selected.name}</span>
            ) : (
              <>
                <span className="truncate">Custom model slug…</span>
                {value && <span className="ml-2 truncate text-xs text-muted">{value}</span>}
              </>
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      {/* Full trigger width: Radix Popover exposes the anchor (trigger) size
          as --radix-popper-anchor-width on the content element. The 22rem
          fallback only applies if the var is somehow unset. */}
      <PopoverContent align="start" className="w-[var(--radix-popper-anchor-width,22rem)] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search models…"
            value={query}
            onValueChange={setQuery}
          />
          <CommandList className="visible-scrollbar">
            {loading && (
              <div className="py-6 text-center text-sm text-muted">Loading models…</div>
            )}
            {!loading && filtered.length === 0 && (
              <div className="p-3 text-center text-xs text-muted">
                {models.length === 0 ? (message ?? "No models available") : "No models match"}
              </div>
            )}
            {!loading && (
              <CommandGroup>
                {filtered.map((m) => (
                  <CommandItem key={m.id} value={m.id} onSelect={() => pick(m.id)}>
                    <Check className={cn("mr-1 h-4 w-4 shrink-0", value === m.id ? "opacity-100" : "opacity-0")} />
                    {/* Stacked layout — NO truncation: the name must be fully
                        visible even for long ones (e.g. "NVIDIA: Nemotron 3.5
                        ASR Streaming Multilingual 0.6B"). Name wraps; pricing
                        stays right-aligned on the top row; slug below. */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="font-medium">{m.name}</span>
                        <span className="shrink-0 whitespace-nowrap text-xs text-muted">
                          {formatPricing(m.pricing, isTranscription)}
                        </span>
                      </div>
                      <div className="text-xs text-muted">{m.id}</div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            <CommandGroup>
              <CommandItem value={CUSTOM_MODEL} onSelect={() => pick(CUSTOM_MODEL)}>
                <Check className={cn("mr-1 h-4 w-4 shrink-0", isCustom ? "opacity-100" : "opacity-0")} />
                <span className="font-medium">Custom model slug…</span>
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
